import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signIn, signOut, getCurrentUser } from 'aws-amplify/auth';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { AtSign, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import securityLogger from '@/services/SecurityLogger';
import rateLimiter from '@/services/RateLimiter';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { checkAuthState } = useAuth();
  
  // Get the return URL from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/app/dashboard';
  
  // Note: Session persistence is handled by AWS Amplify Auth
  // No need for manual localStorage credential storage
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    // Get client IP
    const clientIP = await getClientIP();
    
    // Check rate limiting before attempting login
    const rateLimitResult = rateLimiter.checkRateLimit(formData.email, clientIP);
    if (!rateLimitResult.allowed) {
      setIsLoading(false);
      setErrors({ general: rateLimitResult.message });
      
      toast({
        title: 'Access Temporarily Restricted',
        description: rateLimitResult.message,
        variant: 'error'
      });
      
      // Log the rate limit event
      await securityLogger.logSecurityEvent({
        event: 'RATE_LIMIT_EXCEEDED',
        email: formData.email,
        ipAddress: clientIP,
        details: {
          reason: rateLimitResult.reason,
          remainingAttempts: rateLimitResult.remainingAttempts
        },
        severity: 'HIGH'
      });
      
      return;
    }
    
    // Log login attempt
    const loginAttemptData = {
      email: formData.email,
      success: false, // Will be updated based on result
      ipAddress: clientIP,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    
    try {
      // Check if there's already a signed-in user and sign them out first
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          await signOut();
        }
      } catch (error) {
        // No current user, continue with sign in
      }

      const { isSignedIn, nextStep } = await signIn({
        username: formData.email,
        password: formData.password,
      });

      if (isSignedIn) {
        // Successful login
        loginAttemptData.success = true;
        await securityLogger.logLoginAttempt(loginAttemptData);
        
        // Record successful attempt (resets rate limit counters)
        rateLimiter.recordSuccessfulAttempt(formData.email, clientIP);

        // Note: Session persistence is handled by AWS Amplify Auth automatically
        // The rememberMe state is kept for UI purposes only

        // Refresh auth state
        await checkAuthState();
        
        toast({
          title: 'Welcome back!',
          description: 'You have successfully signed in.',
          variant: 'success'
        });
        
        // Redirect to the page the user was trying to access, or dashboard
        navigate(from, { replace: true });
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        // User needs to confirm their email
        loginAttemptData.success = false;
        loginAttemptData.errorCode = 'USER_NOT_CONFIRMED';
        await securityLogger.logLoginAttempt(loginAttemptData);
        
        navigate('/verify-email', { 
          state: { 
            email: formData.email,
            from: 'login'
          } 
        });
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA') {
        // MFA required - redirect to MFA verification
        loginAttemptData.success = false; // Not fully successful yet
        loginAttemptData.mfaRequired = true;
        await securityLogger.logLoginAttempt(loginAttemptData);
        
        navigate('/mfa-verification', {
          state: {
            email: formData.email,
            from: from
          }
        });
      }
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Log failed attempt
      loginAttemptData.success = false;
      loginAttemptData.errorCode = error.name;
      await securityLogger.logLoginAttempt(loginAttemptData);
      
      // Record failed attempt (increments rate limit counters)
      rateLimiter.recordFailedAttempt(formData.email, clientIP);
      
      let errorMessage = 'An error occurred during sign in.';
      
      if (error.name === 'UserNotConfirmedException') {
        errorMessage = 'Your account is not confirmed. Please check your email for a confirmation link.';
      } else if (error.name === 'NotAuthorizedException') {
        errorMessage = 'Incorrect email or password.';
      } else if (error.name === 'UserNotFoundException') {
        errorMessage = 'No account found with this email address.';
      } else if (error.name === 'TooManyRequestsException') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.name === 'UserAlreadyAuthenticatedException') {
        // Handle case where user is already authenticated
        try {
          await signOut();
          errorMessage = 'You were already signed in. Please try signing in again.';
        } catch (signOutError) {
          errorMessage = 'There was an issue with your existing session. Please refresh and try again.';
        }
      }

      setErrors({ general: errorMessage });
      
      toast({
        title: 'Sign In Failed',
        description: errorMessage,
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get client IP (simplified)
  const getClientIP = async () => {
    // In a real implementation, this would be passed from the server
    // For now, return a placeholder
    return 'client-ip-placeholder';
  };
  
  return (
    <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
        <p className="text-gray-600 mt-2">Welcome back to your account</p>
      </div>
      
      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{errors.general}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <AtSign className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`block w-full pl-10 pr-3 py-2 border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              className={`block w-full pl-10 pr-10 py-2 border ${
                errors.password ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>
          
          <div className="text-sm">
            <Link to="/forgot-password" className="text-primary hover:text-primary-dark">
              Forgot password?
            </Link>
          </div>
        </div>
        
        <div>
          <Button
            type="submit"
            isLoading={isLoading}
            className="w-full"
          >
            Sign In
          </Button>
        </div>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="text-primary hover:text-primary-dark font-medium">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
